import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, User, Heart, Menu, X, LogOut, Shield, Plus, Play, Download, MessageSquarePlus, MapPinPlus, Users, Rss, Timer, Flame, Settings } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Mascot } from "@/components/Mascot";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { HeaderDialogs, type HeaderDialogsHandle } from "@/components/layout/HeaderDialogs";
import { NotificationBell } from "@/components/notifications";
import { useAuth } from "@/hooks/useAuth";
import { useAdminRole } from "@/hooks/useAdminRole";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { useWorkTimerContext } from "@/components/timer";
import { useStreak } from "@/hooks/useStreak";

function StreakBadge() {
  const { data: streak } = useStreak();
  if (!streak || streak.currentStreak <= 0) return null;
  return (
    <span
      className="absolute -bottom-1 -right-1 flex items-center gap-0.5 rounded-full px-1 py-0.5 text-[10px] font-bold leading-none"
      style={{
        backgroundColor: "hsl(15 80% 50%)",
        color: "hsl(0 0% 100%)",
        boxShadow: "0 1px 3px hsl(15 80% 30% / 0.3)",
      }}
    >
      🔥{streak.currentStreak}
    </span>
  );
}

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dialogsRef = useRef<HeaderDialogsHandle>(null);

  const { user, signOut } = useAuth();
  const { isAdmin } = useAdminRole(false);
  const { canInstall, isIOS, isInstalled } = usePWAInstall();
  const { timerState } = useWorkTimerContext();

  // Fetch profile data from profiles table for consistent display name
  const { data: profile } = useQuery({
    queryKey: ['header-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('profiles')
        .select('display_name, username, avatar_url')
        .eq('id', user.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const location = useLocation();
  const navigate = useNavigate();

  const showInstallButton = !isInstalled && (canInstall || isIOS);

  const handleSignOut = async () => {
    await signOut();
    navigate("/browse");
  };

  const navItems = [
    { href: "/browse", label: "Home", icon: Home },
    { href: "/feed", label: "Feed", icon: Rss },
  ];

  const isActive = (path: string) => location.pathname === path;

  const getUserInitials = () => {
    const name = profile?.display_name || profile?.username || user?.user_metadata?.display_name || user?.email || "U";
    return name.charAt(0).toUpperCase();
  };

  const getUserDisplayName = () => {
    return (
      profile?.display_name ||
      profile?.username ||
      user?.user_metadata?.display_name ||
      user?.user_metadata?.full_name ||
      user?.email?.split("@")[0] ||
      "User"
    );
  };

  const getUserAvatarUrl = () => {
    return profile?.avatar_url || user?.user_metadata?.avatar_url;
  };

  return (
     <header className="sticky top-0 z-50 w-full border-b border-border/30 glass-frost header-scroll-shadow" style={{ paddingTop: 'env(safe-area-inset-top)', backgroundClip: 'border-box' }}>
       {/* Opaque safe-area fill so status bar matches nav */}
       <div className="absolute inset-x-0 top-0 bg-background" style={{ height: 'env(safe-area-inset-top)' }} />
       <div className="container flex h-14 sm:h-16 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group">
          <Mascot mood="happy" size="logo" />
          <span className="font-display text-xl font-bold text-logo">grounded</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-2 ml-8">
          {navItems.map(({ href, label, icon: Icon }) => (
            <Link
              key={href}
              to={href}
              className={cn(
                "relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                isActive(href)
                  ? "bg-secondary text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
              {isActive(href) && (
                <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full" />
              )}
            </Link>
          ))}
          {isAdmin && (
            <Button onClick={() => dialogsRef.current?.openAddCafe()} size="sm" className="ml-2 gap-2">
              <Plus className="h-4 w-4" />
              Add Cafe
            </Button>
          )}
          {showInstallButton && (
            <Button 
              onClick={() => dialogsRef.current?.openInstall()} 
              variant="outline" 
              size="sm" 
              className="ml-2 gap-2"
            >
              <Download className="h-4 w-4" />
              Install
            </Button>
          )}
          {user && !isAdmin && (
            <>
              <Button 
                onClick={() => dialogsRef.current?.openFeedback()} 
                variant="outline" 
                size="sm" 
                className="ml-2 gap-2"
              >
                <MessageSquarePlus className="h-4 w-4" />
                Feedback
              </Button>
              <Button 
                onClick={() => dialogsRef.current?.openSuggestCafe()} 
                variant="outline" 
                size="sm" 
                className="ml-2 gap-2"
              >
                <MapPinPlus className="h-4 w-4" />
                Suggest Cafe
              </Button>
            </>
          )}
        </nav>

        {/* Auth Buttons / User Menu */}
        <div className="hidden lg:flex items-center gap-3">
          {user && timerState.isRunning && (
            <div className="flex items-center gap-1 text-sage">
              <Timer className="h-4 w-4 animate-pulse" />
            </div>
          )}
          {user && <NotificationBell />}
          {user && !isAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2 min-h-[44px] min-w-[44px]"
                  onClick={() => dialogsRef.current?.openSuggestCafe()}
                  aria-label="Suggest a Café"
                >
                  <MapPinPlus className="h-5 w-5 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Suggest a Café</TooltipContent>
            </Tooltip>
          )}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 px-2 min-h-[44px] min-w-[44px]">
                  <div className="relative">
                    <Avatar className="h-8 w-8">
                    <AvatarImage src={getUserAvatarUrl()} />
                      <AvatarFallback className="text-sm">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <StreakBadge />
                  </div>
                  <span className="text-sm font-medium">{getUserDisplayName()}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {isAdmin && (
                  <>
                    <DropdownMenuItem
                      onClick={() => dialogsRef.current?.openAddCafe()}
                      className="flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Cafe
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/?onboarding=1" className="flex items-center gap-2">
                        <Play className="h-4 w-4" />
                        Preview Onboarding
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/favorites" className="flex items-center gap-2">
                    <Heart className="h-4 w-4" />
                    Favorites
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/profile?settings=1')}
                  className="flex items-center gap-2"
                >
                  <Settings className="h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/auth">
                <Button variant="outline" size="sm">
                  Sign In
                </Button>
              </Link>
              <Link to="/auth?mode=signup">
                <Button size="sm" className="btn-cozy text-primary-foreground rounded-lg">
                  Join Community
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Mobile Header Icons */}
        <div className="lg:hidden flex items-center gap-1.5">
          {user && timerState.isRunning && (
            <div className="flex items-center text-sage">
              <Timer className="h-4 w-4 animate-pulse" />
            </div>
          )}
          {user && <NotificationBell />}
          {user && !isAdmin && (
            <button
              className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground active:scale-95 transition-all"
              onClick={() => dialogsRef.current?.openSuggestCafe()}
              aria-label="Suggest a Café"
            >
              <MapPinPlus className="h-5 w-5" />
            </button>
          )}
          {!user && (
            <>
              <Link to="/auth">
                <Button variant="ghost" size="sm" className="text-xs px-2 h-8">
                  Sign In
                </Button>
              </Link>
              <Link to="/auth?mode=signup">
                <Button size="sm" className="btn-cozy text-primary-foreground rounded-lg text-xs px-3 h-8">
                  Join Community
                </Button>
              </Link>
            </>
          )}
          <button
            className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground active:scale-95 transition-all"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {user ? (
              <div className="relative">
                <Avatar className="h-7 w-7">
                  <AvatarImage src={getUserAvatarUrl()} />
                  <AvatarFallback className="text-xs">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <StreakBadge />
              </div>
            ) : (
              mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl">
          <nav className="container py-4 space-y-2">
            {/* Home/Feed live in the mobile bottom nav bar */}

            {isAdmin && (
              <button
                onClick={() => {
                  dialogsRef.current?.openAddCafe();
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 transition-colors w-full"
              >
                <Plus className="h-5 w-5" />
                Add Cafe
              </button>
            )}
            {showInstallButton && (
              <button
                onClick={() => {
                  void dialogsRef.current?.openInstall();
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors w-full"
              >
                <Download className="h-5 w-5" />
                Install App
              </button>
            )}
            {user && !isAdmin && (
              <>
                <button
                  onClick={() => {
                    dialogsRef.current?.openSuggestCafe();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground bg-secondary/60 hover:bg-secondary transition-colors w-full"
                >
                  <MapPinPlus className="h-5 w-5" />
                  Suggest a Cafe
                </button>
                <button
                  onClick={() => {
                    dialogsRef.current?.openFeedback();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors w-full"
                >
                  <MessageSquarePlus className="h-5 w-5" />
                  Send Feedback
                </button>
              </>
            )}
            <div className="pt-4 border-t border-border flex flex-col gap-2">
              {user ? (
                <>
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={getUserAvatarUrl()} />
                      <AvatarFallback>
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="font-medium text-foreground">{getUserDisplayName()}</span>
                      <span className="text-xs text-muted-foreground">View Profile</span>
                    </div>
                  </Link>
                  <Link
                    to="/favorites"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                  >
                    <Heart className="h-5 w-5" />
                    Favorites
                  </Link>
                  {isAdmin && (
                    <>
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                      >
                        <Shield className="h-5 w-5" />
                        Admin Dashboard
                      </Link>
                      <Link
                        to="/?onboarding=1"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                      >
                        <Play className="h-5 w-5" />
                        Preview Onboarding
                      </Link>
                    </>
                  )}
                  <Button
                    variant="outline"
                    className="w-full text-destructive"
                    onClick={async () => {
                      await handleSignOut();
                      setMobileMenuOpen(false);
                    }}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">Sign In</Button>
                  </Link>
                  <Link to="/auth?mode=signup" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full btn-cozy text-primary-foreground">Join Community</Button>
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}

      <HeaderDialogs ref={dialogsRef} />
    </header>
  );
}
